import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        red: 'bg-SeabiscuitMainThemeColor text-white hover:bg-SeabiscuitMainThemeColorDark',
        outline:
          'border border-input border-solid bg-background hover:bg-SeabiscuitMainThemeColor/5 hover:text-SeabiscuitMainThemeColor hover:border-SeabiscuitMainThemeColor/5',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-SeabiscuitMainThemeColor/5 hover:text-SeabiscuitMainThemeColor',
        light:
          'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor hover:bg-SeabiscuitMainThemeColor hover:text-white',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        underline: 'underline hover:no-underline bg-black/[3%] text-black/50 hover:text-black',
      },
      size: {
        default: 'px-4 py-2 [&_svg]:size-4',
        sm: 'rounded-md px-3 [&_svg]:size-3',
        lg: 'rounded-md px-8 [&_svg]:size-5',
        icon: 'rounded-full h-10 w-10 [&_svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
